import React, {useState, createContext, useEffect, useCallback, useMemo} from 'react';
import { collection, getDocs, getDoc, doc, setDoc, deleteDoc, addDoc } from "firebase/firestore";
import { db } from '../firebase-config';
import { getAuth } from "firebase/auth";
import { GoogleGenerativeAI } from '@google/generative-ai';

export const AppCTX = createContext();

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

const getCurrentDay = () => new Date().getDay();

export const AppData = ({children, userProfile}) => {
    const [menu, setMenu] = useState(null);
    const [mealPlan, setMealPlan] = useState(null)
    const [selectedDay, setSelectedDay] = useState(()=>getCurrentDay());
    const [workouts, setWorkouts] = useState([])
    const [editingMode, setEditingMode] = useState(false)
    const [workoutplan, setWorkoutPlan] = useState([])
    const [dailySchedule, setDailySchedule] = useState(null);
    const [videoRecommendations, setVideoRecommendations] = useState(null);
    const [videoRecommendationsLoading, setVideoRecommendationsLoading] = useState(false);

    useEffect(() => {
        console.log('userProfile: ', userProfile);
        setMealPlan(userProfile.mealPlan);
        getUserWorkouts(userProfile.uid);
    }, [userProfile]);


    const getUser = useCallback(async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);   
        return docSnap.data();
    }, []);

    const generateAIContent = useCallback(async (prompt, model = "gemini-2.0-flash") => {
        const aiModel = genAI.getGenerativeModel({ 
            model,
            generationConfig: { responseMimeType: "application/json" }
        });
        const result = await aiModel.generateContent(prompt);
        const response = await result.response;
        return JSON.parse(response.text());
    }, []);
    
    // ----------------------------------------------------------------------
    // ------------------- USER MEAL PLANNING FUNCTIONS ---------------------
    // ----------------------------------------------------------------------

    const optimizeDailyMeals = async (dayPlan, targetMacros) => {
        console.log('proompting Ai')
        const prompt = `
            Given the following daily meal plan and target macros, please modify each recipe so that all 3 meals combined should sum up to 70-80% of the required Daily Target.
            Modify ingredients and instructions as needed, but try to keep the essence of each meal.
            Provide the response as a JSON object.

            Daily Meal Plan:
            Breakfast: ${JSON.stringify(dayPlan.meals.breakfast)}
            Lunch: ${JSON.stringify(dayPlan.meals.lunch)}
            Dinner: ${JSON.stringify(dayPlan.meals.dinner)}

            Target Daily Macros:
            Calories: ${targetMacros.targetCalories}
            Protein: ${targetMacros.targetProteins}g
            Carbs: ${targetMacros.targetCarbs}g
            Fat: ${targetMacros.targetFat}g

            For each meal, provide:
            1. Modified ingredients (if necessary)
            2. Modified directions (if necessary)
            3. Estimated macros for this recipe 

            Response format:
            {
                "breakfast": {
                    "directions": "string",
                    "ingredients": [array of strings],
                    "estimatedMacros": {
                        "calories": number,
                        "protein": number,
                        "carbs": number,
                        "fat": number
                    }
                },
                "lunch": {
                    // Same structure as breakfast
                },
                "dinner": {
                    // Same structure as breakfast
                },
                "totalDailyMacros": {
                    "calories": number,
                    "protein": number,
                    "carbs": number,
                    "fat": number
                },
                "remainingMacros": {
                    "calories": number,
                    "protein": number,
                    "carbs": number,
                    "fat": number
                }
            }
        `;
        return generateAIContent(prompt);
    };

    const sanitizeFirebaseData = (obj) => {
        if (obj === undefined || obj === null) {
            return null;
        }
        
        if (typeof obj !== 'object') {
            return obj;
        }
        
        if (Array.isArray(obj)) {
            return obj.map(item => sanitizeFirebaseData(item));
        }
        
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value === undefined) {
                sanitized[key] = null;
            } else {
                sanitized[key] = sanitizeFirebaseData(value);
            }
        }
        
        return sanitized;
    };

    const generateMealPlan = async () => {
        const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const mealTimes = ['breakfast', 'lunch', 'dinner'];
        
        console.log('generateMealPlan', menu);
        let menuBook = menu;
        if (menuBook === null) {
            menuBook = await getMenu();
        }
    
        // Step 1: Create a random meal plan
        const createRandomMealPlan = () => {
            return daysOfWeek.reduce((plan, day, index) => {
                const dayPlan = { day, meals: {} };
                mealTimes.forEach(mealTime => {
                    const mealOptions = menuBook.filter(item => item.time === mealTime);
                    if (mealOptions.length) {
                        const randomIndex = Math.floor(Math.random() * mealOptions.length);
                        dayPlan.meals[mealTime] = mealOptions[randomIndex];
                    }
                });
                plan[index] = dayPlan;
                return plan;
            }, {});
        };
    
        // Generate the initial random meal plan
        const initialMealPlan = createRandomMealPlan();
        console.log('initial mealplan', initialMealPlan);
    
        // Optimize each day's meals
        const optimizedWeeklyPlan = {};
        for (let i = 0; i < daysOfWeek.length; i++) {
            const dayPlan = initialMealPlan[i];
            const optimizedDay = await optimizeDailyMeals(dayPlan, userProfile.targetMacros);
            
            // Merge the optimized data back into the day plan
            optimizedWeeklyPlan[i] = {
                day: dayPlan.day,
                meals: {},
                totalDailyMacros: optimizedDay.totalDailyMacros,
                remainingMacros: optimizedDay.remainingMacros
            };
    
            for (const mealTime of mealTimes) {
                optimizedWeeklyPlan[i].meals[mealTime] = {
                    ...dayPlan.meals[mealTime],
                    directions: optimizedDay[mealTime].directions,
                    ingredients: optimizedDay[mealTime].ingredients,
                    estimatedMacros: optimizedDay[mealTime].estimatedMacros
                };
            }
        }
    
        console.log('Optimized Weekly Meal Plan:', optimizedWeeklyPlan);
    
        try {
            // Before saving to Firebase, sanitize the data
            const sanitizedMealPlan = sanitizeFirebaseData(optimizedWeeklyPlan);
            
            // Update the local state
            setMealPlan(sanitizedMealPlan);
            
            // Update in Firebase
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const userRef = doc(db, "users", user.uid);
                await setDoc(userRef, { mealPlan: sanitizedMealPlan }, { merge: true });
                console.log("Meal plan generated and saved successfully");
                return sanitizedMealPlan;
            }
        } catch (error) {
            console.error("Error generating meal plan: ", error);
            throw error;
        }
    };

    const modifyMealPlan = async (newMealPlan) => {
        try {
            // Sanitize the meal plan to replace undefined values with null
            const sanitizedMealPlan = sanitizeFirebaseData(newMealPlan);
            
            // Update the local state
            setMealPlan(sanitizedMealPlan);
            
            // Update in Firebase
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const userRef = doc(db, "users", user.uid);
                await setDoc(userRef, { mealPlan: sanitizedMealPlan }, { merge: true });
                console.log("Meal plan updated successfully");
            }
        } catch (error) {
            console.error("Error adding meal plan to Firebase: ", error);
            throw error;
        }
    };

    // ------------------------------------------------------------------------------------
    // ------------------------- GLOBAL RECIPIE BOOK ---------------------------------------
    // ------------------------------------------------------------------------------------
    
    const getMenu = async () => {
        let res = []
        const menuBook = await getDocs(collection(db, "RecipieBook"));
        menuBook.forEach((doc) => {
            res.push({...doc.data(), id:doc.id });
        });
        console.log('menuBook', res);
        setMenu(res)
        return res;
    }

    // ----- Adding and deleting meals from the menu
    const createMeal = async (payload, time, itemName) => {
        console.log('ADDING MEAL', {payload, time, itemName})
        try{
            await setDoc(doc(db, time, itemName), payload);
        }catch(err){
            console.warn(err)
        }
        getMenu()
    }

    const saveChangesToRecipieBook = async (newData) => {
        // loop through newData 
        for (let index in newData) {
            // if object has no id 
            console.log('data', newData[index])
            if (newData[index].status === 'new'){
                // add new workout to collection called Workouts
                delete newData[index].status
                console.log('data added', newData[index])
                addDoc(collection(db, "RecipieBook"), newData[index])
            }else if (newData[index].status === 'changed'){
                // update existing document in collection called Workouts
                let idx= newData[index].id
                delete newData[index].status
                delete newData[index].id
                console.log('changed Data', newData[index])
                await setDoc(doc(db, "RecipieBook", idx), newData[index]);
            }else if (newData[index].status === 'delete'){
                // delete existing document in collection called Workouts
                deleteDoc(doc(db, "RecipieBook", newData[index].id))
            }
        }
    }

    const deleteMeal = (meal, time) => {
        
    }
    // ----------------------------------------------------------------------
    
    // ------------------ USER WORKOUT PLANNING FUNCTIONS -------------------
    const getUserWorkouts = async (userID) => {
        let res = []
        const workoutPlan = await getDocs(collection(db, "users/"+userID+"/workoutplan"));
        workoutPlan.forEach((doc) => {
            res.push({...doc.data(), id:doc.id });
        });
        console.log('workoutplan', res);
        setWorkoutPlan(res)
    }

    const modifyworkoutplan = async (item, daysScheduled) => {
        const payload = item;
        payload.days = daysScheduled
        console.log(payload)
        try {
           // Reference to the specific document in the user's mealplan subcollection
            const workoutDocRef = doc(db, `${'users/'+userProfile.uid+'/workoutplan/'+item.id}`);
    
            // Set the document data. This will create the document if it doesn't exist,
            // or update it if it does.
            await setDoc(workoutDocRef, payload, { merge: true });  // Return the new document ID
            getUserWorkouts(userProfile.uid)
        } catch (error) {
            console.error("Error adding recipe to mealplan: ", error);
            throw error;
        }
    };
    // ------------------------------------------------------------------------------------------------
    
    // ------------------------- GLOBAL WORKOUTS PINNED --------------------------------------
    const getWorkouts = async () => {
        const workouts = []
        let querySnapshot = await getDocs(collection(db, "Workouts"));
        querySnapshot.forEach((doc) => {
            console.log({...doc.data(), id:doc.id })
            workouts.push({...doc.data(), id:doc.id });
        });
        setWorkouts(workouts)
    }

    const saveWorkouts = async (newData) => {
        console.log('new Data Collection', newData)
        // loop through newData 
        for (let index in newData) {
            // if object has no id 
            console.log('data',newData[index])
            if (newData[index].status === 'new'){
                // add new workout to collection called Workouts
                delete newData[index].status
                console.log('data added', newData[index])
                addDoc(collection(db, "Workouts"), newData[index])
            }else if (newData[index].status === 'changed'){
                // update existing document in collection called Workouts
                let idx= newData[index].id
                delete newData[index].status
                delete newData[index].id
                console.log('changed Data', newData[index])
                await setDoc(doc(db, "Workouts", idx), newData[index]);
            }else if (newData[index].status === 'delete'){
                // delete existing document in collection called Workouts
                deleteDoc(doc(db, "Workouts", newData[index].id))
            }
        }
        getWorkouts()
    }

    // ----------------------------------------------------------------------

    // ------------ Ai Assistant functions --------------------------------
    
    const getVideoRecommendations = useCallback(async () => {
        if (videoRecommendations) return videoRecommendations;
        if (videoRecommendationsLoading) return null;

        setVideoRecommendationsLoading(true);
        try {
            console.log('proompting Ai')
            const prompt = `
                Based on the following training routine, suggest 5 types of workout training video to follow.
                Format your response as a JSON object with the following structure:
                {
                    "videoTypes": [
                        {
                            "type": "string",
                            "description": "string"
                        }
                    ]
                }
                
                Training routine: ${JSON.stringify(userProfile?.recommendedTraining?.weeklyRoutine)}
            `;
            const parsedResponse = await generateAIContent(prompt);
            
            if (parsedResponse && parsedResponse.videoTypes) {
                setVideoRecommendations(parsedResponse.videoTypes);
                return parsedResponse.videoTypes;
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error getting recommendations:', error);
            const errorRecommendations = [{ type: 'Error', description: 'Failed to get recommendations. Please try again.' }];
            setVideoRecommendations(errorRecommendations);
            return errorRecommendations;
        } finally {
            setVideoRecommendationsLoading(false);
        }
    }, [userProfile, generateAIContent, videoRecommendations, videoRecommendationsLoading]);

    useEffect(() => {
        const getDailySchedule = async () => {
            if (dailySchedule !== null) return;

            try {
                console.log('proompting Ai')
                const prompt = `
                    Based on the following training advice and fitness goals, create a detailed daily schedule with habits to help the user achieve their goals.
                    Format your response as a JSON object with the following structure:
                    {
                        "morningRoutine": [
                        { "time": "string", "activity": "string", "description": "string" }
                        ],
                        "afternoonRoutine": [
                        { "time": "string", "activity": "string", "description": "string" }
                        ],
                        "eveningRoutine": [
                        { "time": "string", "activity": "string", "description": "string" }
                        ],
                        "nightRoutine": [
                        { "time": "string", "activity": "string", "description": "string" }
                        ],
                        "motivationalMessage": "string"
                    }
                    
                    Include activities for exercise, meals, work/study, mental exercises, and relaxation.
                    Training advice: ${userProfile?.recommendedTraining}
                    Fitness goals: ${userProfile?.goals}
                `;
                const parsedResponse = await generateAIContent(prompt);
                
                if (parsedResponse && parsedResponse.morningRoutine) {
                    setDailySchedule(parsedResponse);
                } else {
                    throw new Error('Invalid response format');
                }
            } catch (error) {
                console.error('Error getting daily schedule:', error);
                setDailySchedule({ 
                    morningRoutine: [{ time: 'Error', activity: 'Failed to get schedule. Please try again.', description: '' }],
                    afternoonRoutine: [{ time: 'Error', activity: 'Failed to get schedule. Please try again.', description: '' }],
                    eveningRoutine: [{ time: 'Error', activity: 'Failed to get schedule. Please try again.', description: '' }],
                    nightRoutine: [{ time: 'Error', activity: 'Failed to get schedule. Please try again.', description: '' }],
                    motivationalMessage: 'Keep pushing forward, both in body and mind!'
                });
            } 
        };
        getDailySchedule();
    }, [userProfile, generateAIContent, dailySchedule]);
    
    
    // Get groccery list
    const getGroccerylist = async () =>{
        const allIngredients = [];

        // Loop through each recipe
        mealPlan.forEach(recipe => {
            // Check if the recipe has an ingredients array
            if (Array.isArray(recipe.ingredients)) {
                // Add all ingredients from this recipe to the allIngredients array
                allIngredients.push(...recipe.ingredients);
            }
        });

        console.log(allIngredients)
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        const genAI = new GoogleGenerativeAI(apiKey);
        const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
        const prompt = "consolidate all the items in the array : " + allIngredients + " into a groccery list. add up duplicate items you find. for example: [1 liter milk , 1.5 liter milk] => 2.5 liters of milk";
        const result = await model.generateContent(prompt);
        console.log(result.response.text());
    }
    // ----------------------------------------------------------------------

    // ---------- Progress Tracker -----------------------------------------
    const markWorkoutCompleted = async ( workoutId, workoutTitle) => {
        const userWorkoutsRef = doc(db, "userProgress", userProfile.uid);
        const timestamp = Date.now();
      
        try {
          // First, get the current user's workout document
          const docSnap = await getDoc(userWorkoutsRef);
      
          if (docSnap.exists()) {
            // If the document exists, update it
            const data = docSnap.data();
            const completedWorkouts = data.completedWorkouts || [];
      
            // Check if this workout is already marked as completed for today
            const today = new Date().setHours(0, 0, 0, 0);
            const workoutIndex = completedWorkouts.findIndex(
              w => w.workoutId === workoutId && w.completedAt.toDate().setHours(0, 0, 0, 0) === today
            );
      
            if (workoutIndex > -1) {
              // If it's already completed today, remove it (toggle off)
              completedWorkouts.splice(workoutIndex, 1);
            } else {
              // If it's not completed today, add it
              const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
              completedWorkouts.push({
                workoutId,
                workoutTitle,
                completedOn: days[selectedDay],
                completedAt: timestamp
              });
            }
      
            // Update the document
            await setDoc(userWorkoutsRef, { completedWorkouts }, { merge: true });
          } else {
            // If the document doesn't exist, create it
            await setDoc(userWorkoutsRef, {
              completedWorkouts: [{
                workoutId,
                workoutTitle,
                completedAt: timestamp
              }]
            });
          }
      
          console.log("Workout completion status updated successfully");
        } catch (error) {
          console.error("Error updating workout completion status:", error);
          throw error;
        }
      };

      const contextValue = useMemo(() => ({
        menu, 
        mealPlan, 
        deleteMeal: () => {}, 
        createMeal, 
        getGroccerylist, 
        getUser, 
        selectedDay, 
        setSelectedDay, 
        workouts, 
        setWorkouts,
        editingMode, 
        setEditingMode, 
        saveWorkouts, 
        saveChangesToRecipieBook,
        userProfile, 
        modifyMealPlan, 
        generateMealPlan, 
        modifyworkoutplan, 
        workoutplan,
        getMenu, 
        getWorkouts, 
        markWorkoutCompleted, 
        dailySchedule, 
        setDailySchedule,
        videoRecommendations, 
        videoRecommendationsLoading, 
        getVideoRecommendations
    }), [menu, mealPlan, createMeal, getGroccerylist, getUser, selectedDay, workouts, 
        editingMode, saveWorkouts, saveChangesToRecipieBook, userProfile, modifyMealPlan, 
        generateMealPlan, modifyworkoutplan, workoutplan, getMenu, getWorkouts, 
        markWorkoutCompleted, dailySchedule, videoRecommendations, 
        videoRecommendationsLoading, getVideoRecommendations]);
    
        return (
            <AppCTX.Provider value={contextValue}>
                {children}
            </AppCTX.Provider>
        );
}